import {
  FilterPrimitive,
  IndexedFilters,
  IndexedNestedFilter,
  FilterRange,
} from '../types/filters';
import { isTypeIndexedNestedFilter, isTypeNumberRange } from './filter-menu-type-guards';

const RANGE_FILTERS = ['mileage', 'miles_per_gallon', 'monthly_price'];

interface ConvertSearchParamsToFiltersProps {
  searchParams: URLSearchParams;
  availableFilters: IndexedFilters;
}

function convertSearchParamsToFilters({
  searchParams,
  availableFilters,
}: ConvertSearchParamsToFiltersProps): IndexedFilters {
  const filters: IndexedFilters = {};

  for (const [param, value] of searchParams.entries()) {
    // If the parameter is makes, we need to parse it differently
    if (param === 'makes') {
      const allMakes = isTypeIndexedNestedFilter(availableFilters?.make)
        ? availableFilters.make
        : {};

      filters['makes'] = parseMakeModelsFilter(value, allMakes);
    } else if (RANGE_FILTERS.includes(param)) {
      filters[param] = parseFilterRange(value);
    }
    // Skip sort_option and check if the parameter is a valid filter, then parse it
    else if (
      param !== 'sort_option' &&
      // Note: Doing availableFilters.hasOwnProperty(param) throws a Lint error
      Object.prototype.hasOwnProperty.call(availableFilters, param)
    ) {
      filters[param] = parseFilterValues(value);
    }
  }

  return filters;
}

/**
 * Util to parse makes filters from URLSearchParams.
 * The makes filter is NestedFilter which is a special case.
 *
 * This example of the makes filter:
 * Toyota:Camry;Prius,Chevrolet
 *
 * Would be parsed into the following object:
 * { Toyota: ['Camry', 'Prius'], Chevrolet: [] }
 *
 * In the case of Chevrolet, we consider consider the empty models array as the user selecting all models.
 * So, we look up the available models for Chevrolet in the available filters and attach them.
 *
 * If we have the following available filters:
 * { Toyota: ['Camry', 'Prius'], Chevrolet: ['Malibu', 'Cruze'] }
 *
 * The final parsed object would be:
 * { Toyota: ['Camry', 'Prius'], Chevrolet: ['Malibu', 'Cruze'] }
 *
 * @param value
 * @param allMakes
 * @returns
 */
export function parseMakeModelsFilter(
  value: string,
  allMakes: IndexedNestedFilter
): IndexedNestedFilter {
  const makes: IndexedNestedFilter = {};
  const makeModelPairs = value.split(',');

  for (const pair of makeModelPairs) {
    const [make, modelsString] = pair.split(':');

    if (make) {
      const models = modelsString ? modelsString.split(';') : (allMakes[make] ?? []);
      makes[make] = models;
    }
  }

  return makes;
}

/**
 * Util to parse SingleFilter values from URLSearchParams.
 * @param value
 * @returns
 */
export function parseFilterValues(value: string): FilterPrimitive[] {
  return value
    .split(',')
    .filter((item) => item.trim() !== '')
    .map(convertValue);
}

/**
 * Util to parse Range filter values from URLSearchParams.
 * @param value
 * @returns
 */
export function parseFilterRange(value: string): FilterRange<number> {
  const range = value.split(',');
  const min = parseInt(range[0]);
  const max = parseInt(range[1]);

  // -1 min and max is a fallback range to represent an invalid range. The backend
  // will remove filters with ranges that have -1 for min and max so this should be safe to do.
  if (isNaN(min) || isNaN(max)) {
    return { min: -1, max: -1 };
  }

  return { min, max };
}

/**
 * Convert a string value to a number if possible.
 * @param value - The value to convert.
 * @returns The converted value or the original value if it couldn't be converted.
 */
export function convertValue(value: string): FilterPrimitive {
  if (value === '') {
    return value;
  }

  // First, try parsing as an integer
  // Compare the string representation of the parsed integer to the original string.
  // This ensures that we only accept whole numbers without any decimal points or exponents.
  const intValue = parseInt(value);
  if (!isNaN(intValue) && intValue.toString() === value) {
    return intValue;
  }

  // If not an integer, try parsing as a float.
  // Compare the string representation of the parsed float to the original string.
  // This step allows for decimal numbers. but still excludes scientific notation.
  const floatValue = parseFloat(value);
  if (!isNaN(floatValue) && floatValue.toString() === value) {
    return floatValue;
  }

  // If no parsing methods worked, return the original string
  return value;
}

interface ConvertFiltersToSearchParamsProps {
  selectedFilters: IndexedFilters;
  searchParams?: URLSearchParams;
}

function convertFiltersToSearchParams({
  searchParams: currentSearchParams,
  selectedFilters,
}: ConvertFiltersToSearchParamsProps): URLSearchParams {
  const searchParams = new URLSearchParams();

  // Add back sort_option if it exists
  const sortOption = currentSearchParams?.get('sort_option');

  if (sortOption) {
    searchParams.append('sort_option', sortOption);
  }

  // Convert filters to search params
  for (const [category, value] of Object.entries(selectedFilters)) {
    if (category === 'makes' && isTypeIndexedNestedFilter(value)) {
      const makesSearchParam = convertMakesToSearchParam(value);
      searchParams.append('makes', makesSearchParam);
    } else if (Array.isArray(value)) {
      searchParams.append(category, value.join(','));
    } else if (isTypeNumberRange(value)) {
      searchParams.append(category, `${value.min},${value.max}`);
    }
  }

  return searchParams;
}

/**
 * Util to convert an IndexedNestedFilter to a string representation.
 * @param makeModels - The IndexedNestedFilter object representing make and models.
 * @returns The string representation of the make and models.
 */
function convertMakesToSearchParam(makeModels: IndexedNestedFilter): string {
  const makeModelPairs: string[] = [];

  for (const [make, models] of Object.entries(makeModels)) {
    const modelString = models.join(';');
    makeModelPairs.push(`${make}:${modelString}`);
  }

  return makeModelPairs.join(',');
}

export { convertSearchParamsToFilters, convertFiltersToSearchParams };
